import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useDispatch, useSelector } from 'react-redux';
import { deleteItem, fetchItems, postItem, putItem } from '../../../support/queries';
import { RootState } from '../../../support/store';
import { checkCiscoProductsLoaded } from '../../../support/slices/productsSlice';
import { CacheKey } from '../../../types/cache-keys';
import type { EscalationBuRequestMapping } from './EscalationBuRequestMappingEditDialog';
import {
  AUTOMATION_ASSISTANCE_INVALID_CREATE_FIELDS,
  BU_REQUEST_TYPE_NAMES,
  BuRequestTypeName,
  getDefaultFieldValuesForRequestType,
  getInlineConfigDefaultsForProductRequestType,
  getRequestModeConfigForProductAndRequestType,
  getSupportedRequestTypeNamesForProduct,
  isBuRequestTypeName,
  JIRA_FIELD_DEFINITIONS,
} from './buJiraConstants';
import {
  EMPTY_SELECT_VALUE,
  getRelationId,
  hasSelectedId,
  normalizeNullableString,
  parseSelectValue,
  RelationValue,
  SelectId,
  unpackItems,
} from './escalationFormUtils';

type Product = {
  id: number;
  name: string;
};

type AssignmentType = {
  id: number;
  name: string;
  level: number;
};

type GroupOption = {
  id: number | string;
  name: string;
  group_id?: string;
};

export type EscalationProductSupport = {
  id?: number;
  product?: { id?: number; name?: string } | number | null;
  assignment_type?: { id?: number; name?: string; level?: number } | number | null;
  user_group?: { id?: number; name?: string } | null;
  azure_ad_group?: { id?: number; name?: string } | null;
  group_source?: 'local' | 'azure_ad' | 'both' | 'external';
  team_name?: string | null;
  team_email?: string | null;
  is_active?: boolean;
  updated_by?: string | null;
};

interface Props {
  open: boolean;
  editItem: EscalationProductSupport | null;
  onClose: () => void;
  readOnly?: boolean;
}

type InlineBuMappingConfig = {
  service_desk_id: string;
  project_id: string;
  project_key: string;
  project_name: string;
  request_type_id: string;
  request_create_key: string;
  default_field_values_text: string;
  priority: number;
  notes: string;
};

type ProductSupportPayload = {
  id?: number;
  product_id: number;
  assignment_type_id: number;
  team_name: string | null;
  team_email: string | null;
  user_group_id: number | null;
  azure_ad_group_id: number | null;
  group_source: 'local' | 'azure_ad' | 'both' | 'external';
  is_active: boolean;
};

const GROUP_SOURCE_OPTIONS = [
  { value: 'local', label: 'Local User Group' },
  { value: 'azure_ad', label: 'Azure AD Group' },
  { value: 'both', label: 'Both (Local + Azure AD)' },
] as const;

const getMappingProductId = (mapping: EscalationBuRequestMapping): SelectId => {
  return getRelationId(mapping.product);
};

const getMappingAssignmentTypeId = (mapping: EscalationBuRequestMapping): SelectId => {
  return getRelationId(mapping.assignment_type);
};

const getMappingEscalationTypeId = (mapping: EscalationBuRequestMapping): SelectId => {
  return getRelationId(mapping.escalation_type);
};

const getProductSupportProductId = (route: EscalationProductSupport): SelectId => {
  return getRelationId(route.product as RelationValue);
};

const getProductSupportAssignmentTypeId = (route: EscalationProductSupport): SelectId => {
  return getRelationId(route.assignment_type as RelationValue);
};

const getProductSupportUserGroupId = (route: EscalationProductSupport): SelectId => {
  return getRelationId(route.user_group as RelationValue);
};

const getProductSupportAzureAdGroupId = (route: EscalationProductSupport): SelectId => {
  return getRelationId(route.azure_ad_group as RelationValue);
};

const productSupportRouteMatchesPayload = (
  route: EscalationProductSupport,
  payload: ProductSupportPayload
): boolean => {
  const routeProductId = getProductSupportProductId(route);
  const routeAssignmentTypeId = getProductSupportAssignmentTypeId(route);
  const routeUserGroupId = getProductSupportUserGroupId(route);
  const routeAzureAdGroupId = getProductSupportAzureAdGroupId(route);

  return (
    routeProductId === payload.product_id &&
    routeAssignmentTypeId === payload.assignment_type_id &&
    normalizeNullableString(route.team_name) === payload.team_name &&
    normalizeNullableString(route.team_email) === payload.team_email &&
    (hasSelectedId(routeUserGroupId) ? routeUserGroupId : null) === payload.user_group_id &&
    (hasSelectedId(routeAzureAdGroupId) ? routeAzureAdGroupId : null) ===
    payload.azure_ad_group_id &&
    (route.group_source ?? 'local') === payload.group_source &&
    (route.is_active ?? true) === payload.is_active
  );
};

const hasRequiredMappingTemplateFields = (
  mapping?: EscalationBuRequestMapping | null
): boolean => {
  if (!mapping) return false;

  return Boolean(
    mapping.service_desk_id != null &&
    String(mapping.service_desk_id).trim() &&
    mapping.project_id != null &&
    String(mapping.project_id).trim() &&
    mapping.project_key &&
    String(mapping.project_key).trim() &&
    mapping.project_name &&
    String(mapping.project_name).trim() &&
    mapping.request_type_id != null &&
    String(mapping.request_type_id).trim() &&
    mapping.request_type_name &&
    String(mapping.request_type_name).trim()
  );
};

const buildInlineConfigFromMapping = (
  mapping?: EscalationBuRequestMapping | null,
  requestTypeName?: BuRequestTypeName,
  productId?: SelectId
): InlineBuMappingConfig => {
  const catalogDefaults = getInlineConfigDefaultsForProductRequestType(
    productId ?? EMPTY_SELECT_VALUE,
    requestTypeName || ''
  );

  const defaultFieldValues =
    mapping?.default_field_values ||
    getDefaultFieldValuesForRequestType(requestTypeName || '');

  return {
    service_desk_id:
      mapping?.service_desk_id != null
        ? String(mapping.service_desk_id)
        : catalogDefaults.service_desk_id,

    project_id:
      mapping?.project_id != null ? String(mapping.project_id) : catalogDefaults.project_id,

    project_key: mapping?.project_key ?? catalogDefaults.project_key,
    project_name: mapping?.project_name ?? catalogDefaults.project_name,

    request_type_id:
      mapping?.request_type_id != null
        ? String(mapping.request_type_id)
        : catalogDefaults.request_type_id,

    request_create_key: mapping?.request_create_key ?? catalogDefaults.request_create_key,

    default_field_values_text: JSON.stringify(defaultFieldValues, null, 2),

    priority: mapping?.priority ?? catalogDefaults.priority,
    notes: mapping?.notes ?? catalogDefaults.notes,
  };
};

const buildMappingUpdatePayload = (
  mapping: EscalationBuRequestMapping,
  overrides: Partial<Record<string, any>> = {}
) => {
  const escalationTypeId = getMappingEscalationTypeId(mapping);

  return {
    id: mapping.id,

    product: getMappingProductId(mapping),
    escalation_type: hasSelectedId(escalationTypeId) ? escalationTypeId : null,
    assignment_type: getMappingAssignmentTypeId(mapping),

    service_desk_id: mapping.service_desk_id,
    project_id: mapping.project_id,
    project_key: mapping.project_key,
    project_name: mapping.project_name,

    request_type_id: mapping.request_type_id,
    request_type_name: mapping.request_type_name,
    request_create_key: mapping.request_create_key,

    default_field_values: mapping.default_field_values || {},
    is_active: mapping.is_active ?? true,
    priority: mapping.priority ?? 100,
    notes: mapping.notes ?? null,

    ...overrides,
  };
};

const buildMappingCreatePayloadFromTemplate = ({
                                                 productId,
                                                 assignmentTypeId,
                                                 template,
                                               }: {
  productId: number;
  assignmentTypeId: number;
  template: EscalationBuRequestMapping;
}) => {
  return {
    product: productId,
    escalation_type: null,
    assignment_type: assignmentTypeId,

    service_desk_id: template.service_desk_id,
    project_id: template.project_id,
    project_key: template.project_key,
    project_name: template.project_name,

    request_type_id: template.request_type_id,
    request_type_name: template.request_type_name,
    request_create_key: template.request_create_key || null,

    default_field_values: template.default_field_values || {},
    is_active: true,
    priority: template.priority ?? 100,
    notes: template.notes ?? null,
  };
};

const buildMappingPayloadFromInlineConfig = ({
                                               productId,
                                               assignmentTypeId,
                                               requestTypeName,
                                               config,
                                             }: {
  productId: number;
  assignmentTypeId: number;
  requestTypeName: BuRequestTypeName;
  config: InlineBuMappingConfig;
}) => {
  return {
    product: productId,
    escalation_type: null,
    assignment_type: assignmentTypeId,

    service_desk_id: config.service_desk_id,
    project_id: config.project_id,
    project_key: config.project_key,
    project_name: config.project_name,

    request_type_id: config.request_type_id,
    request_type_name: requestTypeName,
    request_create_key: config.request_create_key || null,

    default_field_values: JSON.parse(config.default_field_values_text || '{}'),
    is_active: true,
    priority: config.priority,
    notes: config.notes || null,
  };
};

const EscalationProductSupportEditDialog: React.FC<Props> = ({
                                                               open,
                                                               editItem,
                                                               onClose,
                                                               readOnly = false,
                                                             }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const products = useSelector((state: RootState) => state.products.ciscoOnly);
  const isProductsLoading = useSelector(
    (state: RootState) => !!state.products.isCiscoProductsLoading
  );

  const [productId, setProductId] = useState<SelectId>(EMPTY_SELECT_VALUE);
  const [assignmentTypeId, setAssignmentTypeId] = useState<SelectId>(EMPTY_SELECT_VALUE);
  const [teamName, setTeamName] = useState('');
  const [teamEmail, setTeamEmail] = useState('');
  const [userGroupId, setUserGroupId] = useState<SelectId>(EMPTY_SELECT_VALUE);
  const [azureAdGroupId, setAzureAdGroupId] = useState<SelectId>(EMPTY_SELECT_VALUE);
  const [groupSource, setGroupSource] = useState<'local' | 'azure_ad' | 'both' | 'external'>(
    'local'
  );
  const [isActive, setIsActive] = useState(true);

  const [assignmentTypes, setAssignmentTypes] = useState<AssignmentType[]>([]);
  const [userGroups, setUserGroups] = useState<GroupOption[]>([]);
  const [azureAdGroups, setAzureAdGroups] = useState<GroupOption[]>([]);
  const [buRequestMappings, setBuRequestMappings] = useState<EscalationBuRequestMapping[]>([]);
  const [productSupportRoutes, setProductSupportRoutes] = useState<EscalationProductSupport[]>([]);

  const [selectedBuRequestTypeNames, setSelectedBuRequestTypeNames] = useState<BuRequestTypeName[]>(
    []
  );

  const [inlineMappingConfigs, setInlineMappingConfigs] = useState<
    Partial<Record<BuRequestTypeName, InlineBuMappingConfig>>
  >({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    dispatch(checkCiscoProductsLoaded() as never);
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    Promise.all([
      fetchItems('db/escalation-assignment-type'),
      fetchItems('db/user-group'),
      fetchItems('db/azure-ad-group'),
      fetchItems('db/escalation-bu-request-mapping', { detailed: true, depth: 1 }),
      fetchItems('db/escalation-product-support', { detailed: true, depth: 2 }),
    ])
      .then(
        ([
           assignmentTypesResp,
           userGroupsResp,
           azureAdGroupsResp,
           buMappingsResp,
           productSupportResp,
         ]) => {
          const nextAssignmentTypes = unpackItems<AssignmentType>(assignmentTypesResp);
          const nextUserGroups = unpackItems<GroupOption>(userGroupsResp);
          const nextAzureAdGroups = unpackItems<GroupOption>(azureAdGroupsResp);
          const nextBuMappings = unpackItems<EscalationBuRequestMapping>(buMappingsResp);
          const nextProductSupportRoutes =
            unpackItems<EscalationProductSupport>(productSupportResp);

          setAssignmentTypes(nextAssignmentTypes);
          setUserGroups(nextUserGroups);
          setAzureAdGroups(nextAzureAdGroups);
          setBuRequestMappings(nextBuMappings);
          setProductSupportRoutes(nextProductSupportRoutes);

          setProductId(getRelationId(editItem?.product as RelationValue));
          setAssignmentTypeId(getRelationId(editItem?.assignment_type as RelationValue));
          setUserGroupId(getRelationId(editItem?.user_group as RelationValue));
          setAzureAdGroupId(getRelationId(editItem?.azure_ad_group as RelationValue));

          setGroupSource(editItem?.group_source ?? 'local');
          setTeamName(editItem?.team_name ?? '');
          setTeamEmail(editItem?.team_email ?? '');
          setIsActive(editItem?.is_active ?? true);
          setInlineMappingConfigs({});
        }
      )
      .finally(() => setLoading(false));
  }, [open, editItem]);

  const selectedAssignmentType = useMemo(
    () => assignmentTypes.find((x) => x.id === assignmentTypeId),
    [assignmentTypes, assignmentTypeId]
  );

  const isStage3 = selectedAssignmentType?.level === 3;

  const supportedBuRequestTypeNames = useMemo(() => {
    if (!isStage3 || !hasSelectedId(productId)) return [];

    const supported = getSupportedRequestTypeNamesForProduct(productId);

    return supported.length > 0 ? supported : BU_REQUEST_TYPE_NAMES;
  }, [isStage3, productId]);

  useEffect(() => {
    if (!isStage3) return;

    setGroupSource('external');
    setUserGroupId(EMPTY_SELECT_VALUE);
    setAzureAdGroupId(EMPTY_SELECT_VALUE);
    setTeamName('');
    setTeamEmail('');
  }, [isStage3]);

  const existingRouteForCurrentSelection = useMemo(() => {
    if (!hasSelectedId(productId) || !hasSelectedId(assignmentTypeId)) {
      return undefined;
    }

    return productSupportRoutes.find((route) => {
      const routeProductId = getProductSupportProductId(route);
      const routeAssignmentTypeId = getProductSupportAssignmentTypeId(route);

      return routeProductId === productId && routeAssignmentTypeId === assignmentTypeId;
    });
  }, [productSupportRoutes, productId, assignmentTypeId]);

  const matchingManagedBuMappings = useMemo(() => {
    if (!hasSelectedId(productId) || !hasSelectedId(assignmentTypeId)) return [];

    return buRequestMappings.filter((mapping) => {
      const requestTypeName = mapping.request_type_name || '';

      return (
        getMappingProductId(mapping) === productId &&
        getMappingAssignmentTypeId(mapping) === assignmentTypeId &&
        isBuRequestTypeName(requestTypeName) &&
        supportedBuRequestTypeNames.includes(requestTypeName as BuRequestTypeName)
      );
    });
  }, [buRequestMappings, productId, assignmentTypeId, supportedBuRequestTypeNames]);

  const matchingManagedBuMappingsKey = useMemo(() => {
    return matchingManagedBuMappings
      .map((mapping) => {
        return [
          mapping.id,
          mapping.request_type_name,
          mapping.is_active === false ? 'inactive' : 'active',
          mapping.service_desk_id,
          mapping.project_id,
          mapping.project_key,
          mapping.project_name,
          mapping.request_type_id,
        ].join(':');
      })
      .join('|');
  }, [matchingManagedBuMappings]);

  useEffect(() => {
    if (!isStage3) {
      setSelectedBuRequestTypeNames([]);
      return;
    }

    const existingSelectedNames = matchingManagedBuMappings
      .filter((mapping) => mapping.is_active !== false)
      .map((mapping) => mapping.request_type_name)
      .filter((name): name is BuRequestTypeName => Boolean(name) && isBuRequestTypeName(name));

    setSelectedBuRequestTypeNames(existingSelectedNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStage3, matchingManagedBuMappingsKey]);

  const requestTypeTemplatesByName = useMemo(() => {
    const templates = new Map<BuRequestTypeName, EscalationBuRequestMapping>();

    for (const name of supportedBuRequestTypeNames) {
      const exactCurrentRouteTemplate = matchingManagedBuMappings.find(
        (mapping) => mapping.request_type_name === name && hasRequiredMappingTemplateFields(mapping)
      );

      if (exactCurrentRouteTemplate) {
        templates.set(name, exactCurrentRouteTemplate);
        continue;
      }

      const globalTemplate = buRequestMappings.find(
        (mapping) => mapping.request_type_name === name && hasRequiredMappingTemplateFields(mapping)
      );

      if (globalTemplate) {
        templates.set(name, globalTemplate);
      }
    }

    return templates;
  }, [buRequestMappings, matchingManagedBuMappings, supportedBuRequestTypeNames]);

  const existingRouteMappingsByName = useMemo(() => {
    const result = new Map<BuRequestTypeName, EscalationBuRequestMapping[]>();

    for (const mapping of matchingManagedBuMappings) {
      const name = mapping.request_type_name;
      if (!name || !isBuRequestTypeName(name)) continue;

      const rows = result.get(name) ?? [];
      rows.push(mapping);
      result.set(name, rows);
    }

    return result;
  }, [matchingManagedBuMappings]);

  const requestTypesNeedingInlineConfig = useMemo(() => {
    if (!isStage3) return [];

    return selectedBuRequestTypeNames.filter((name) => {
      const existingRows = existingRouteMappingsByName.get(name) ?? [];
      const primaryExistingRow = existingRows[0];

      if (primaryExistingRow && !hasRequiredMappingTemplateFields(primaryExistingRow)) {
        return true;
      }

      if (!primaryExistingRow && !requestTypeTemplatesByName.has(name)) {
        return true;
      }

      return false;
    });
  }, [
    isStage3,
    selectedBuRequestTypeNames,
    existingRouteMappingsByName,
    requestTypeTemplatesByName,
  ]);

  const requestTypesNeedingInlineConfigKey = useMemo(
    () => requestTypesNeedingInlineConfig.join('|'),
    [requestTypesNeedingInlineConfig]
  );

  useEffect(() => {
    if (!isStage3) return;

    setInlineMappingConfigs((previous) => {
      const next = { ...previous };

      for (const name of requestTypesNeedingInlineConfig) {
        if (next[name]) continue;

        const existingRow = existingRouteMappingsByName.get(name)?.[0];
        const fallbackTemplate = requestTypeTemplatesByName.get(name);

        next[name] = buildInlineConfigFromMapping(
          existingRow || fallbackTemplate || null,
          name,
          productId
        );
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStage3,
    requestTypesNeedingInlineConfigKey,
    existingRouteMappingsByName,
    requestTypeTemplatesByName,
    productId,
  ]);

  const updateInlineMappingConfig = (
    requestTypeName: BuRequestTypeName,
    patch: Partial<InlineBuMappingConfig>
  ) => {
    setInlineMappingConfigs((previous) => {
      const current =
        previous[requestTypeName] ??
        buildInlineConfigFromMapping(null, requestTypeName, productId);

      return {
        ...previous,
        [requestTypeName]: {
          ...current,
          ...patch,
        },
      };
    });
  };

  const requiresLocalGroup = useMemo(
    () => !isStage3 && (groupSource === 'local' || groupSource === 'both'),
    [isStage3, groupSource]
  );

  const requiresAzureAdGroup = useMemo(
    () => !isStage3 && (groupSource === 'azure_ad' || groupSource === 'both'),
    [isStage3, groupSource]
  );

  const validationMessage = useMemo(() => {
    if (!hasSelectedId(productId)) return 'Product is required.';
    if (!hasSelectedId(assignmentTypeId)) return 'Routing option is required.';

    if (isStage3) {
      if (selectedBuRequestTypeNames.length === 0) {
        return 'Select at least one BU request type for this Level 3 route.';
      }

      const unsupported = selectedBuRequestTypeNames.filter(
        (name) => !supportedBuRequestTypeNames.includes(name)
      );

      if (unsupported.length > 0) {
        return `Unsupported request type for this product: ${unsupported.join(', ')}.`;
      }

      for (const requestTypeName of requestTypesNeedingInlineConfig) {
        const config = inlineMappingConfigs[requestTypeName];

        if (!config) return `Configure Jira metadata for ${requestTypeName}.`;
        if (!config.service_desk_id.trim()) return `Service Desk ID is required for ${requestTypeName}.`;
        if (!config.project_id.trim()) return `Project ID is required for ${requestTypeName}.`;
        if (!config.project_key.trim()) return `Project Key is required for ${requestTypeName}.`;
        if (!config.project_name.trim()) return `Project Name is required for ${requestTypeName}.`;
        if (!config.request_type_id.trim()) return `Request Type ID is required for ${requestTypeName}.`;

        try {
          JSON.parse(config.default_field_values_text || '{}');
        } catch {
          return `Default Field Values JSON is invalid for ${requestTypeName}.`;
        }
      }

      return '';
    }

    if (groupSource === 'external') {
      return 'External group source is only valid for Level 3 / Business Unit routing.';
    }

    if (groupSource === 'local' && !hasSelectedId(userGroupId)) {
      return 'Local User Group is required when Group Source is Local User Group.';
    }

    if (groupSource === 'azure_ad' && !hasSelectedId(azureAdGroupId)) {
      return 'Azure AD Group is required when Group Source is Azure AD Group.';
    }

    if (groupSource === 'both' && (!hasSelectedId(userGroupId) || !hasSelectedId(azureAdGroupId))) {
      return 'Both Local User Group and Azure AD Group are required when Group Source is Both.';
    }

    return '';
  }, [
    productId,
    assignmentTypeId,
    isStage3,
    selectedBuRequestTypeNames,
    supportedBuRequestTypeNames,
    requestTypesNeedingInlineConfig,
    inlineMappingConfigs,
    groupSource,
    userGroupId,
    azureAdGroupId,
  ]);

  const syncBuRequestMappingRows = async () => {
    if (!isStage3) return;

    if (!hasSelectedId(productId) || !hasSelectedId(assignmentTypeId)) {
      throw new Error('Product and assignment type are required before syncing BU mappings.');
    }

    const selectedSet = new Set(selectedBuRequestTypeNames);
    const rowsToDelete: EscalationBuRequestMapping[] = [];

    for (const row of matchingManagedBuMappings) {
      const name = row.request_type_name || '';

      if (!isBuRequestTypeName(name) || !selectedSet.has(name)) {
        rowsToDelete.push(row);
      }
    }

    const operations: Promise<any>[] = [];

    for (const row of rowsToDelete) {
      if (row.id != null) {
        operations.push(deleteItem(`db/escalation-bu-request-mapping/${row.id}/`));
      }
    }

    for (const requestTypeName of selectedBuRequestTypeNames) {
      const existingRows = existingRouteMappingsByName.get(requestTypeName) ?? [];
      const primaryExistingRow = existingRows[0];
      const duplicateRows = existingRows.slice(1);

      for (const duplicate of duplicateRows) {
        if (duplicate.id != null) {
          operations.push(deleteItem(`db/escalation-bu-request-mapping/${duplicate.id}/`));
        }
      }

      const needsInlineConfig = requestTypesNeedingInlineConfig.includes(requestTypeName);
      const inlineConfig = inlineMappingConfigs[requestTypeName];

      if (primaryExistingRow) {
        if (needsInlineConfig) {
          if (!inlineConfig) {
            throw new Error(`Configure Jira metadata for ${requestTypeName}.`);
          }

          operations.push(
            putItem(
              'db/escalation-bu-request-mapping',
              buildMappingUpdatePayload(primaryExistingRow, {
                ...buildMappingPayloadFromInlineConfig({
                  productId,
                  assignmentTypeId,
                  requestTypeName,
                  config: inlineConfig,
                }),
                id: primaryExistingRow.id,
              })
            )
          );
        } else if (primaryExistingRow.is_active === false) {
          operations.push(
            putItem(
              'db/escalation-bu-request-mapping',
              buildMappingUpdatePayload(primaryExistingRow, { is_active: true })
            )
          );
        }

        continue;
      }

      const template = requestTypeTemplatesByName.get(requestTypeName);

      if (template) {
        operations.push(
          postItem(
            'db/escalation-bu-request-mapping',
            buildMappingCreatePayloadFromTemplate({
              productId,
              assignmentTypeId,
              template,
            })
          )
        );

        continue;
      }

      if (!inlineConfig) {
        throw new Error(`Configure Jira metadata for ${requestTypeName}.`);
      }

      operations.push(
        postItem(
          'db/escalation-bu-request-mapping',
          buildMappingPayloadFromInlineConfig({
            productId,
            assignmentTypeId,
            requestTypeName,
            config: inlineConfig,
          })
        )
      );
    }

    await Promise.all(operations);
  };

  const handleSave = async () => {
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    if (!hasSelectedId(productId) || !hasSelectedId(assignmentTypeId)) {
      alert('Product and Routing Option are required.');
      return;
    }

    setSaving(true);

    try {
      const productSupportPayload: ProductSupportPayload = {
        product_id: productId,
        assignment_type_id: assignmentTypeId,

        team_name: isStage3 ? null : normalizeNullableString(teamName),
        team_email: isStage3 ? null : normalizeNullableString(teamEmail),

        user_group_id: isStage3 ? null : hasSelectedId(userGroupId) ? userGroupId : null,
        azure_ad_group_id: isStage3 ? null : hasSelectedId(azureAdGroupId) ? azureAdGroupId : null,
        group_source: isStage3 ? 'external' : groupSource,

        is_active: isActive,
      };

      const existingRouteForSelectedProductAndAssignment = existingRouteForCurrentSelection;

      const targetRoute = editItem?.id ? editItem : existingRouteForSelectedProductAndAssignment;

      if (
        editItem?.id &&
        existingRouteForSelectedProductAndAssignment?.id &&
        existingRouteForSelectedProductAndAssignment.id !== editItem.id
      ) {
        throw new Error(
          'A routing entry already exists for this product and assignment type. Edit that existing route instead.'
        );
      }

      if (targetRoute?.id) {
        const payloadWithId = {
          id: targetRoute.id,
          ...productSupportPayload,
        };

        if (!productSupportRouteMatchesPayload(targetRoute, productSupportPayload)) {
          await putItem('db/escalation-product-support', payloadWithId);
        }
      } else {
        await postItem('db/escalation-product-support', productSupportPayload);
      }

      await syncBuRequestMappingRows();

      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationProductSupport'] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationBuRequestMappings'] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.EscalationRequest] });

      onClose();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to save product escalation routing.');
    } finally {
      setSaving(false);
    }
  };

  const sortedUserGroups = useMemo(
    () => [...userGroups].sort((a, b) => a.name.localeCompare(b.name)),
    [userGroups]
  );

  const sortedAzureAdGroups = useMemo(
    () => [...azureAdGroups].sort((a, b) => a.name.localeCompare(b.name)),
    [azureAdGroups]
  );

  const isBusy = loading || saving || isProductsLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editItem?.id ? 'Edit Product Escalation Routing' : 'Add Product Escalation Routing'}
      </DialogTitle>

      <DialogContent>
        {isBusy && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        )}


        <TextField
          select
          fullWidth
          margin="normal"
          label="Product"
          value={productId}
          onChange={(e) => setProductId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          required
        >
          {products.map((product: Product) => (
            <MenuItem key={product.id} value={product.id}>
              {product.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Routing Option"
          value={assignmentTypeId}
          onChange={(e) => setAssignmentTypeId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          required
        >
          {assignmentTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name} {type.level ? `(Level ${type.level})` : ''}
            </MenuItem>
          ))}
        </TextField>

        {!editItem?.id && existingRouteForCurrentSelection?.id && (
          <Alert severity="info" sx={{ mt: 2 }}>
            A routing entry already exists for this product and routing option. Saving will update
            existing route #{existingRouteForCurrentSelection.id} instead of creating a duplicate.
          </Alert>
        )}

        {editItem?.id &&
          existingRouteForCurrentSelection?.id &&
          existingRouteForCurrentSelection.id !== editItem.id && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Another routing entry already exists for this product and routing option: route #
              {existingRouteForCurrentSelection.id}. Saving this change is blocked.
            </Alert>
          )}

        {isStage3 ? (
          <>
            <TextField
              fullWidth
              margin="normal"
              label="Group Source"
              value="External Jira / BU Request Mapping"
              disabled
              helperText="Level 3 routes are always external."
            />

            <TextField
              select
              fullWidth
              margin="normal"
              label="BU Request Types"
              value={selectedBuRequestTypeNames}
              onChange={(e) => {
                const value = e.target.value as unknown;
                const nextValue = Array.isArray(value) ? value : String(value).split(',');

                setSelectedBuRequestTypeNames(
                  nextValue
                    .map((item) => String(item))
                    .filter((item): item is BuRequestTypeName => isBuRequestTypeName(item))
                    .filter((name) => supportedBuRequestTypeNames.includes(name))
                );
              }}
              disabled={readOnly || isBusy}
              required
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const selectedValues = selected as string[];

                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedValues.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  );
                },
              }}
              helperText="Select the BU request types supported for this product."
            >
              {supportedBuRequestTypeNames.map((name) => {
                const config = getRequestModeConfigForProductAndRequestType(productId, name);

                return (
                  <MenuItem key={name} value={name}>
                    {config?.request_type_name || name}
                  </MenuItem>
                );
              })}
            </TextField>

            {hasSelectedId(productId) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Request type metadata is loaded from the BU service catalog for this product.
                Selected request types create mapping rows. Removed request types delete mapping
                rows on save.
              </Alert>
            )}

            {requestTypesNeedingInlineConfig.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" fontWeight={600}>
                  Jira Request Type Configuration
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Provide Jira metadata for selected request types that do not already have usable
                  mapping metadata. Values are prefilled from the BU service catalog where possible.
                </Typography>

                {requestTypesNeedingInlineConfig.map((requestTypeName) => {
                  const config =
                    inlineMappingConfigs[requestTypeName] ??
                    buildInlineConfigFromMapping(null, requestTypeName, productId);

                  const catalogConfig = getRequestModeConfigForProductAndRequestType(
                    productId,
                    requestTypeName
                  );

                  return (
                    <Paper key={requestTypeName} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {requestTypeName}
                      </Typography>

                      {catalogConfig?.request_url && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Jira create URL:{' '}
                          <a
                            href={catalogConfig.request_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {catalogConfig.request_url}
                          </a>
                        </Alert>
                      )}

                      <Alert severity="info" sx={{ mb: 2 }}>
                        Default Field Values support raw Jira field IDs such as{' '}
                        <strong>customfield_13500</strong>, or friendly aliases such as{' '}
                        <strong>business_impact</strong>.
                      </Alert>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {JIRA_FIELD_DEFINITIONS.map((field) => (
                          <Chip
                            key={field.fieldId}
                            size="small"
                            label={`${field.label}: ${field.fieldId}`}
                            variant="outlined"
                          />
                        ))}
                      </Box>

                      {requestTypeName === 'Automation Assistance' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Automation Assistance create requests should not include{' '}
                          {AUTOMATION_ASSISTANCE_INVALID_CREATE_FIELDS.join(', ')} in
                          requestFieldValues. The backend filters these fields before Jira create.
                        </Alert>
                      )}

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Service Desk ID"
                        value={config.service_desk_id}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            service_desk_id: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        required
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Project ID"
                        value={config.project_id}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            project_id: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        required
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Project Key"
                        value={config.project_key}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            project_key: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        required
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Project Name"
                        value={config.project_name}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            project_name: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        required
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Request Type ID"
                        value={config.request_type_id}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            request_type_id: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        required
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Request Type Category"
                        value={catalogConfig?.request_type_category || ''}
                        disabled
                        helperText="Derived from the BU service catalog."
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Request Create Key"
                        value={config.request_create_key}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            request_create_key: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        helperText="Optional."
                      />

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={readOnly || isBusy}
                          onClick={() =>
                            updateInlineMappingConfig(requestTypeName, {
                              default_field_values_text: JSON.stringify(
                                getDefaultFieldValuesForRequestType(requestTypeName),
                                null,
                                2
                              ),
                            })
                          }
                        >
                          Use Default Field Values
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          disabled={readOnly || isBusy}
                          onClick={() =>
                            updateInlineMappingConfig(requestTypeName, {
                              default_field_values_text: JSON.stringify(
                                {
                                  business_impact: '14301',
                                  your_role: '12800',
                                },
                                null,
                                2
                              ),
                            })
                          }
                        >
                          Use Friendly Aliases
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          disabled={readOnly || isBusy}
                          onClick={() =>
                            updateInlineMappingConfig(requestTypeName, {
                              default_field_values_text: JSON.stringify(
                                {
                                  customfield_13500: '14301',
                                  customfield_12100: '12800',
                                },
                                null,
                                2
                              ),
                            })
                          }
                        >
                          Use Raw Field IDs
                        </Button>
                      </Box>

                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        margin="normal"
                        label="Default Field Values JSON"
                        value={config.default_field_values_text}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            default_field_values_text: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                        helperText='Example: { "business_impact": "14301", "your_role": "12800" }'
                      />

                      <TextField
                        fullWidth
                        margin="normal"
                        label="Priority"
                        type="number"
                        value={config.priority}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            priority: Number(e.target.value),
                          })
                        }
                        disabled={readOnly || isBusy}
                        helperText="Lower numbers are preferred first."
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        margin="normal"
                        label="Notes"
                        value={config.notes}
                        onChange={(e) =>
                          updateInlineMappingConfig(requestTypeName, {
                            notes: e.target.value,
                          })
                        }
                        disabled={readOnly || isBusy}
                      />
                    </Paper>
                  );
                })}
              </>
            )}
          </>
        ) : (
          <>
            <TextField
              select
              fullWidth
              margin="normal"
              label="Group Source"
              value={groupSource === 'external' ? 'local' : groupSource}
              onChange={(e) =>
                setGroupSource(e.target.value as 'local' | 'azure_ad' | 'both')
              }
              disabled={readOnly || isBusy}
              required
              helperText="Choose whether eligible members come from a local User Group, an Azure AD Group, or both."
            >
              {GROUP_SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              margin="normal"
              label="Local User Group"
              value={userGroupId}
              onChange={(e) => setUserGroupId(parseSelectValue(e.target.value))}
              disabled={readOnly || isBusy}
              required={requiresLocalGroup}
              helperText={
                groupSource === 'local'
                  ? 'Required when Group Source is Local User Group.'
                  : groupSource === 'both'
                    ? 'Required when Group Source is Both.'
                    : 'Optional when not using local groups.'
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>

              {sortedUserGroups.map((group) => (
                <MenuItem key={group.id} value={Number(group.id)}>
                  {group.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              margin="normal"
              label="Azure AD Group"
              value={azureAdGroupId}
              onChange={(e) => setAzureAdGroupId(parseSelectValue(e.target.value))}
              disabled={readOnly || isBusy}
              required={requiresAzureAdGroup}
              helperText={
                groupSource === 'azure_ad'
                  ? 'Required when Group Source is Azure AD Group.'
                  : groupSource === 'both'
                    ? 'Required when Group Source is Both.'
                    : 'Optional when not using Azure AD groups.'
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>

              {sortedAzureAdGroups.map((group) => (
                <MenuItem key={group.id} value={Number(group.id)}>
                  {group.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              disabled={readOnly || isBusy}
              helperText="Display name shown for this routing option."
            />

            <TextField
              fullWidth
              margin="normal"
              label="Team Email"
              value={teamEmail}
              onChange={(e) => setTeamEmail(e.target.value)}
              disabled={readOnly || isBusy}
              helperText="Optional distro or queue email."
            />
          </>
        )}

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={readOnly || isBusy}
            />
          }
          label="Active"
        />

        {!!validationMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {validationMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>

        {!readOnly && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isBusy || !!validationMessage}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EscalationProductSupportEditDialog;